import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  Wrench,
  Sun,
  Hammer,
  Award,
  LifeBuoy,
  FileBadge,
  Phone,
  Star,
  ArrowRight,
  
  Sparkles,
  Zap,
  CloudRain,
  Wind,
  Play,
} from "lucide-react";
import heroVideo from "@/assets/hero-video.mp4";
import workInActionVideo from "@/assets/work-in-action.mp4";
import { useRef, useState } from "react";
import { useHeroFade } from "@/hooks/use-hero-fade";
const whyExperience = pic("why-experience");
const whyLicensed = pic("why-licensed");
const whyOwner = pic("why-owner");
const whyCertified = pic("why-certified");
const whyGuarantee = pic("why-guarantee");
const ctaFinalCover = pic("cta-final-cover");

import { pic } from "@/lib/responsive-images";

const heroPoster = pic("hero-roof-solar").src;
const solarDetail = pic("solar-detail");
const amr3Rooftop = pic("amr3-technician-rooftop");
const svcRoofInstall = pic("svc-roof-install");
const svcRoofRepair = pic("svc-roof-repair");
const svcSolarInstall = pic("svc-solar-install");
const svcIntegratedSolar = pic("svc-integrated-solar");
const svcStormDamage = pic("svc-storm-damage");
const svcGutters = pic("svc-gutters");
const svcHvac = pic("svc-hvac");
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GoogleIcon } from "@/components/GoogleIcon";
import { WaveDivider } from "@/components/WaveDivider";
import { NMServiceArea } from "@/components/NMServiceArea";
import { TravelFeeNote } from "@/components/TravelFeeNote";
import { useI18n } from "@/lib/i18n";

const GOOGLE_BUSINESS_URL =
  "https://www.google.com/maps?cid=6621967010212129613";

export const Route = createFileRoute("/")({
  head: () => {
    const url = "https://www.amr3designconstruction.com";
    const img = `${url}/og-image-v3.jpg`;
    return {
      meta: [
        { title: "AMR 3 Roofing & Solar Contractor in Albuquerque, NM" },
        {
          name: "description",
          content:
            "Albuquerque roofing & solar contractor with 25+ years of craftsmanship. Residential & commercial installs, repairs, storm damage. Request a consultation.",
        },
        { property: "og:title", content: "AMR 3 Roofing & Solar Contractor in Albuquerque, NM" },
        {
          property: "og:description",
          content:
            "Albuquerque roofing & solar contractor with 25+ years of craftsmanship. Residential & commercial installs, repairs, storm damage. Request a consultation.",
        },
        { property: "og:image", content: img },
        { property: "og:image:secure_url", content: img },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "AMR 3 Roofing & Solar — Albuquerque & New Mexico, expert consultations" },
        { property: "og:url", content: `${url}/` },
        { name: "twitter:image", content: img },
        { name: "twitter:title", content: "AMR 3 Roofing & Solar Contractor in Albuquerque, NM" },
        {
          name: "twitter:description",
          content:
            "Albuquerque roofing & solar contractor with 25+ years of craftsmanship. Residential & commercial installs, repairs, storm damage. Request a consultation.",
        },
      ],
      links: [{ rel: "canonical", href: `${url}/` }],
    };
  },
  component: HomePage,
});

function HomePage() {
  const { t } = useI18n();
  const workVideoRef = useRef<HTMLVideoElement>(null);
  const [workVideoPlaying, setWorkVideoPlaying] = useState(false);
  const heroRef = useHeroFade<HTMLElement>();

  const handlePlayWorkVideo = () => {
    const v = workVideoRef.current;
    if (!v) return;
    v.play();
    setWorkVideoPlaying(true);
  };

  const services = [
    { icon: Hammer, key: "1", image: svcRoofInstall, anchor: "roofing" },
    { icon: Wrench, key: "2", image: svcRoofRepair, anchor: "roofing" },
    { icon: Sun, key: "3", image: svcSolarInstall, anchor: "solar" },
    { icon: Zap, key: "4", image: svcIntegratedSolar, anchor: "solar" },
    { icon: CloudRain, key: "5", image: svcStormDamage, anchor: "storm" },
    { icon: ShieldCheck, key: "6", image: svcGutters, anchor: "gutters" },
    { icon: Wind, key: "7", image: svcHvac, anchor: "hvac" },
  ];

  const steps = [1, 2, 3, 4, 5, 6];

  const reasons = [
    { icon: Award, key: "1", image: whyExperience },
    { icon: ShieldCheck, key: "2", image: whyLicensed },
    { icon: Sparkles, key: "3", image: whyOwner },
    { icon: FileBadge, key: "4", image: whyCertified },
    { icon: LifeBuoy, key: "5", image: whyGuarantee },
  ];

  const reviews = [
    {
      name: "Susana",
      avatar:
        "https://lh3.googleusercontent.com/a-/ALV-UjX5d07UKEQQs3CuOiIN5-9pdHb8C4lqtCe72_NbzzUe3-Pf3JuMVA=w86-h86-p-rp-mo-br100",
      body: "We've had several projects done by Andres. He was very professional and gave a fair honest quote quickly. He worked with us to schedule and give us updates throughout the projects. We would highly recommend and plan to keep our business with AMR 3 Design & Construction.",
      time: "4 years ago",
    },
    {
      name: "Nicole Henry",
      avatar:
        "https://lh3.googleusercontent.com/a-/ALV-UjW49MKgQtUMf9CXO0tsNJU4hP8agOgjhlsvTWT5uK2ok5Dq_3dtGg=w86-h86-p-rp-mo-ba3-br100",
      body: "Andres and his brother Adam did a great job! They were super friendly and took their time and made sure everything looked great. They cleaned up after everything. I would definitely hire them again.",
      time: "3 years ago",
    },
    {
      name: "Kassandra Ray",
      avatar:
        "https://lh3.googleusercontent.com/a/ACg8ocKx-WTQVzAnnAnbgvO7Txqie9dYQrKK5iecFi6BhJZqN_98eg=w86-h86-p-rp-mo-br100",
      body: "Replaced/ installed windows. Quality, professionalism, and fair price.",
      time: "4 years ago",
    },
  ];

  const trustChips = [
    { icon: ShieldCheck, label: t("common.licensedInsured") },
    { icon: Award, label: t("common.manufacturerWarranties") },
    { icon: LifeBuoy, label: t("common.emergency247") },
  ];

  return (
    <>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative isolate min-h-[100vh] overflow-hidden will-change-[opacity]"
      >
        <video
          src={heroVideo}
          poster={heroPoster}
          // @ts-expect-error fetchPriority is valid HTML but not yet in React video types
          fetchpriority="high"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-label="AMR 3 roofing and solar projects across New Mexico"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <track
            kind="captions"
            srcLang="en"
            label="English captions"
            src="/captions/hero-video.en.vtt"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.16_0.03_50/0.72)] via-[oklch(0.16_0.03_50/0.48)] to-[oklch(0.16_0.03_50/0.18)]" />

        <div className="relative mx-auto flex min-h-[100vh] max-w-7xl items-center px-4 pt-36 pb-28 sm:px-6 sm:pt-40 sm:pb-24 lg:px-8">
          <div className="max-w-3xl animate-fade-in text-white">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-accent">
              <span className="h-px w-10 bg-accent" />
              <span>{t("common.yearsBadge")}</span>
            </div>
            <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
              {t("hero.h1")}
            </h1>
            <p className="mt-6 max-w-2xl text-base text-white/85 sm:text-lg">{t("hero.sub")}</p>

            <div className="mt-9 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
                <a href="tel:+15058680847">
                  <Phone className="h-4 w-4" /> {t("common.callPhone")}
                </a>
              </Button>
              <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
                <Link to="/contact" hash="project-form">
                  {t("common.requestConsultation")} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap gap-2.5">
              {trustChips.map((c) => {
                const Icon = c.icon;
                return (
                  <span
                    key={c.label}
                    className="flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-medium text-white backdrop-blur"
                  >
                    <Icon className="h-4 w-4 text-accent" /> {c.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

      </section>

      {/* ABOUT */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">
              {t("home.about.title")}
            </h2>
            <p className="mt-4 text-muted-foreground">{t("home.about.sub")}</p>
          </div>
          <div className="mt-12 grid items-stretch gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="relative h-full min-h-[320px] w-full overflow-hidden rounded-2xl shadow-[var(--shadow-elegant)]">
              <img
                src={amr3Rooftop.src}
                srcSet={amr3Rooftop.srcSet}
                sizes="(max-width: 1024px) 100vw, 600px"
                alt="AMR 3 Design & Construction technician overlooking a New Mexico home with integrated roofing and solar at sunset"
                width={1448}
                height={1086}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center text-center lg:text-left">
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                {t("home.about.body")}
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2.5 lg:justify-start">
                {trustChips.map((c) => {
                  const Icon = c.icon;
                  return (
                    <span
                      key={c.label}
                      className="flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-2 text-xs font-medium text-primary"
                    >
                      <Icon className="h-4 w-4 text-accent-strong" /> {c.label}
                    </span>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start">
                <Button asChild variant="gold" size="lg" className="hover-pulse-gold w-full sm:w-auto">
                  <a href="tel:+15058680847">
                    <Phone className="h-4 w-4" /> {t("common.callPhone")}
                  </a>
                </Button>
                <Button asChild variant="goldOutline" size="lg" className="w-full sm:w-auto">
                  <Link to="/about">
                    {t("common.ourStory")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* SERVICES */}
      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />

      <section className="bg-secondary py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-4xl font-bold text-primary sm:text-5xl">{t("home.services.title")}</h2>
            <p className="mt-4 text-muted-foreground">{t("home.services.sub")}</p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {services.slice(0, 4).map((s, i) => {
              const Icon = s.icon;
              const title = t(`home.svc.${s.key}.title`);
              const isLoneLast = false;
              return (
                <div
                  key={s.key}
                  className={cn(
                    "group hover-lift animate-fade-up overflow-hidden rounded-2xl border border-border bg-card flex flex-col",
                    isLoneLast &&
                      "md:col-span-2 md:mx-auto md:w-full md:max-w-md lg:col-span-1 lg:col-start-2 lg:mx-0 lg:max-w-none",
                  )}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >

                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image.src}
                      srcSet={s.image.srcSet}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 300px"
                      alt={title}
                      width={800}
                      height={600}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl bg-card/95 text-accent shadow-md backdrop-blur">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-serif text-xl font-semibold text-primary">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`home.svc.${s.key}.body`)}</p>
                    <Link
                      to="/services"
                      hash={s.anchor}
                      aria-label={`${t("home.services.learnAbout")} ${title}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-strong hover:gap-2.5 transition-all"
                    >
                      {t("home.services.learnAbout")} {title} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex justify-center">
            <Button asChild variant="gold" size="lg" className="hover-pulse-gold w-full sm:w-auto">
              <Link to="/services">
                See All Services <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--secondary)" bottomColor="var(--background)" />

      {/* SEE OUR WORK IN ACTION */}
      <section className="bg-background pt-16 pb-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-4xl font-bold text-primary sm:text-5xl">{t("home.work.title")}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("home.work.body1")}</p>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("home.work.body2")}</p>
          </div>

          <div className="relative mx-auto mt-10 max-w-3xl overflow-hidden rounded-2xl shadow-[var(--shadow-elegant)]">
            <video
              ref={workVideoRef}
              src={workInActionVideo}
              poster={solarDetail.srcSet.split(", ").find((v) => v.endsWith(" 720w"))?.split(" ")[0] ?? solarDetail.src}
              playsInline
              controls={workVideoPlaying}
              preload="metadata"
              className="aspect-video h-full w-full object-cover"
              onEnded={() => setWorkVideoPlaying(false)}
              onPause={() => setWorkVideoPlaying(false)}
              onPlay={() => setWorkVideoPlaying(true)}
            >
              <track
                kind="captions"
                srcLang="en"
                label="English captions"
                src="/captions/work-in-action.en.vtt"
              />
            </video>
            {!workVideoPlaying && (
              <button
                type="button"
                onClick={handlePlayWorkVideo}
                aria-label={t("home.work.playLabel")}
                className="absolute inset-0 flex items-center justify-center bg-primary/10 transition-colors hover:bg-primary/30"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg ring-8 ring-white/30 transition-transform hover:scale-110">
                  <Play className="ml-1 h-7 w-7 fill-current" />
                </span>
              </button>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="goldOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">
                {t("common.requestConsultation")} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />

      {/* HOW WE WORK */}
      <section className="bg-secondary py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-4xl font-bold text-primary sm:text-5xl">{t("home.how.title")}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("home.how.sub")}</p>
          </div>

          <ol className="mt-14 space-y-6">
            {steps.map((n) => (
              <li key={n} className="flex gap-5 rounded-2xl border border-border bg-card p-6 hover-lift">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent font-serif text-xl font-bold text-accent-foreground">
                  {n}
                </div>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary">{t(`home.step.${n}.title`)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t(`home.step.${n}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <TravelFeeNote className="mx-auto max-w-2xl" />
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--secondary)" bottomColor="var(--background)" />


      {/* WHY CHOOSE US */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-4xl font-bold text-primary sm:text-5xl">{t("home.why.title")}</h2>
            <p className="mt-4 text-muted-foreground">{t("home.why.sub")}</p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reasons.slice(0, 3).map((r, i) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.key}
                  className="group hover-lift animate-fade-up overflow-hidden rounded-2xl border border-border bg-card"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.image.src}
                      srcSet={r.image.srcSet}
                      sizes="(max-width: 768px) 100vw, 50vw"
                      alt={t(`home.why.${r.key}.title`)}
                      width={1024}
                      height={768}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl bg-card/95 text-accent shadow-md backdrop-blur">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-lg font-semibold text-primary">{t(`home.why.${r.key}.title`)}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`home.why.${r.key}.body`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {reasons.slice(3).map((r, i) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.key}
                  className="group hover-lift animate-fade-up overflow-hidden rounded-2xl border border-border bg-card"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.image.src}
                      srcSet={r.image.srcSet}
                      sizes="(max-width: 768px) 100vw, 50vw"
                      alt={t(`home.why.${r.key}.title`)}
                      width={1024}
                      height={768}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl bg-card/95 text-accent shadow-md backdrop-blur">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-lg font-semibold text-primary">{t(`home.why.${r.key}.title`)}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`home.why.${r.key}.body`)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Button asChild variant="gold" size="lg" className="hover-pulse-gold">
              <a href="tel:+15058680847">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />

      {/* GOOGLE REVIEWS */}
      <section className="bg-secondary py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-4xl font-bold text-primary sm:text-5xl">{t("home.reviews.title")}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("home.reviews.sub")}</p>
          </div>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-card px-5 py-2.5 shadow-sm">
              <GoogleIcon className="h-6 w-6" />
              <span className="text-sm font-semibold text-primary">Google Rating</span>
              <span className="h-5 w-px bg-border" />
              <span className="flex items-center gap-1">
                <span className="text-sm font-bold text-primary">5.0</span>
                <span className="flex gap-0.5 text-accent">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </span>
              </span>
            </div>
          </div>



          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {reviews.map((r) => (
              <div key={r.name} className="hover-lift rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-3">
                  <img src={r.avatar} srcSet={`${r.avatar.replace(/w86-h86/, "w44-h44")} 1x, ${r.avatar} 2x`} alt={r.name} width={44} height={44} loading="lazy" className="h-11 w-11 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-primary">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.time}</div>
                  </div>
                  <span className="font-serif text-2xl font-bold text-muted-foreground/40">G</span>
                </div>
                <div className="mt-3 flex gap-0.5 text-accent">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">"{r.body}"</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center gap-3">
            <Button asChild variant="gold" size="lg" className="hover-pulse-gold">
              <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" className="gap-2.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
                  <GoogleIcon className="h-3.5 w-3.5" />
                </span>
                See all reviews on Google
              </a>
            </Button>
          </div>

        </div>
      </section>

      <WaveDivider topColor="var(--secondary)" bottomColor="var(--background)" />

      {/* NM SERVICE AREA */}
      <NMServiceArea />

      {/* FINAL CTA */}
      <section className="relative isolate overflow-hidden py-24 sm:py-28">
        <img
          src={ctaFinalCover.src}
          srcSet={ctaFinalCover.srcSet}
          sizes="100vw"
          alt="Modern New Mexico adobe home with a new roof and integrated solar array at golden hour"
          width={1536}
          height={768}
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.16_0.03_50/0.78)] via-[oklch(0.16_0.03_50/0.58)] to-[oklch(0.16_0.03_50/0.38)]" />
        <div className="absolute left-0 right-0 -top-px z-10">
          <WaveDivider flip color="var(--background)" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6">
          <h2 className="font-serif text-4xl font-bold text-white sm:text-5xl">{t("home.cta.title")}</h2>
          <div className="mt-9 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
